import { Link } from "react-router-dom";
import { PostLoginAuthLink } from "@/components/routing/PostLoginAuthLink";
import { Button } from "@/components/ui/button";
import { ArrowRight, LineChart } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { HERO_SAMPLE_DATA_LABEL, HERO_VALUE_PROPOSITION } from "@/lib/landingMarketing";

/**
 * Renders the hero section of a finance dashboard landing page.
 * @example
 * HeroSection()
 * <section>...</section>
 * @returns {JSX.Element} A JSX element representing the hero section.
 * @description
 *   - The section divides into two main parts: text content on the left and an image on the right.
 *   - Includes buttons for navigation to sign-up and learn more pages.
 *   - The background gradient is applied to the main heading text.
 *   - Responsive design adjusts layout and text size for different screen sizes.
 */
export function HeroSection() {
  const { user } = useAuth();
  const isLoggedIn = !!user;

  return (
    <section className="container mx-auto px-4 py-8 md:py-16">
      <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-center max-w-6xl mx-auto">
        <div className="text-center lg:text-left space-y-6">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-50 border border-orange-200 rounded-full text-sm font-medium text-orange-700">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-500"></span>
            </span>
            {HERO_VALUE_PROPOSITION}
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
            <span className="bg-gradient-to-r from-orange-600 to-orange-500 bg-clip-text text-transparent">
              Family Wealth Management,
            </span>
            <br />
            <span className="text-gray-900">All in One Place</span>
          </h1>
          <p className="text-base md:text-lg text-gray-600 leading-relaxed">
            Track household investments across PPF, EPF, NPS, mutual funds, gold, and real estate —
            with member profiles, shared goals, and consolidated family portfolio views.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
            {isLoggedIn ? (
              <Button
                size="lg"
                className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 shadow-lg px-8"
                asChild
              >
                <Link to="/dashboard">
                  Go to Dashboard <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            ) : (
              <>
                <Button
                  size="lg"
                  className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 shadow-lg px-8"
                  asChild
                >
                  <PostLoginAuthLink mode="signup">
                    Start Free <ArrowRight className="ml-2 h-4 w-4" />
                  </PostLoginAuthLink>
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-2 border-orange-300 hover:bg-orange-50"
                  asChild
                >
                  <PostLoginAuthLink>Sign In</PostLoginAuthLink>
                </Button>
              </>
            )}
          </div>
        </div>
        <div className="relative">
          <div
            className="relative rounded-2xl overflow-hidden shadow-xl bg-gradient-to-br from-orange-100 to-white p-6"
            aria-label={`Dashboard preview — ${HERO_SAMPLE_DATA_LABEL}`}
          >
            <div className="absolute top-3 right-3 z-10 rounded-full bg-white/90 px-3 py-1 text-xs font-medium text-gray-600 border border-orange-200 shadow-sm">
              {HERO_SAMPLE_DATA_LABEL}
            </div>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="bg-white rounded-lg p-4 shadow-sm border border-orange-100">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-sm font-medium text-gray-700">Portfolio</span>
                </div>
                <div className="text-2xl font-bold text-gray-900">₹2.4M</div>
                <div className="text-sm text-green-600">+12.5% ↗</div>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-sm border border-orange-100">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                  <span className="text-sm font-medium text-gray-700">Goals</span>
                </div>
                <div className="text-2xl font-bold text-gray-900">5</div>
                <div className="text-sm text-orange-600">3 on track</div>
              </div>
            </div>
            <div className="bg-white rounded-lg p-4 shadow-sm border border-orange-100">
              <LineChart className="w-6 h-6 text-orange-500 mb-2" />
              <p className="text-sm font-medium text-gray-700">Real-time Analytics</p>
              <p className="text-xs text-gray-500">Smart insights powered by AI</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
